// ---------------------------------------------------------------------------
// WF-016 Phase 5 — Workflow Folders / Variables / Goals CQRS Event Catalog
// ---------------------------------------------------------------------------
// Story WF-016: "Workflow folders, variables, goals, step logs, and goal
// progress are exposed through APIs/MCP with permissions."
//
// Decision packet `WF-016-DECISION-PACKET.md` (2026-05-13) maps the 12-file
// build envelope split into 3 phases (Phase 1 DB schema, Phase 2 HTTP
// routes, Phase 3 MCP tools) plus the WF-016 reality-classification packet
// (`WF-016-FOLDERS-VARIABLES-GOALS-PARITY-PACKET.md`) which classifies the
// step-logs surface as VERIFIED (writer + `workflow.logs` MCP read tool
// already exist) and the folders / variables / goals / goal-progress
// surfaces as OVERCLAIM / FALSE / PARTIAL.
//
// This file is the Phase 5 narrow follow-up: it catalogues the CQRS event
// types the future Phase 2 routes MUST emit so workflow_step_logs +
// audit-runner + DLQ recovery have stable hook names BEFORE the HTTP
// routes are mounted. The vocab pins the 3 folder lifecycle events + 3
// variable lifecycle events + 4 goal lifecycle events verbatim so the
// future route emitters can `import` the strings instead of drifting.
//
// Per CLAUDE.md Governance Rule #7 ("Event Catalog"): every event type
// MUST be catalogued before implementation. Pinning the vocabulary now
// means the future emitter PR can `import` the strings + payload types
// instead of hand-rolling them (and drifting).
//
// What this file is NOT:
//  - It does not emit events — that's Phase 2 (HTTP route wiring).
//  - It does not declare `workflow_variables` / `workflow_goals` Drizzle
//    tables (Phase 1 schema build is a separate PR).
//  - It does not register MCP tools (Phase 3 packet).
//
// What it IS:
//  - The single source of truth for `workflow.folder.*` /
//    `workflow.variable.*` / `workflow.goal.*` event strings.
//  - Closed value-type + goal-status vocab the future routes MUST use as
//    the payload discriminant.
//  - Typed payload contracts so emitters + DLQ consumers cannot drift.
//  - Runtime narrowing guards so DLQ / audit-runner reject unknown values.
// ---------------------------------------------------------------------------

/**
 * Closed catalog of WF-016 CQRS event types covering the 3 surfaces that
 * need new schema (folders binding + variables + goals). Step-logs has
 * its own writer (PR #786) and `workflow.logs` MCP read tool already.
 *
 * Versioned with `@v1` suffix per platform convention. One business fact
 * per event (Governance Rule #6).
 */
export const WF_016_WORKFLOW_FOLDER_EVENT_TYPES = [
  "workflow.folder.created@v1",
  "workflow.folder.updated@v1",
  "workflow.folder.deleted@v1",
] as const;

export const WF_016_WORKFLOW_VARIABLE_EVENT_TYPES = [
  "workflow.variable.defined@v1",
  "workflow.variable.updated@v1",
  "workflow.variable.deleted@v1",
] as const;

export const WF_016_WORKFLOW_GOAL_EVENT_TYPES = [
  "workflow.goal.defined@v1",
  "workflow.goal.updated@v1",
  "workflow.goal.deleted@v1",
  "workflow.goal.progressed@v1",
] as const;

/**
 * Aggregate of all WF-016 event types for DLQ / audit-runner narrowing.
 */
export const WF_016_EVENT_TYPES = [
  ...WF_016_WORKFLOW_FOLDER_EVENT_TYPES,
  ...WF_016_WORKFLOW_VARIABLE_EVENT_TYPES,
  ...WF_016_WORKFLOW_GOAL_EVENT_TYPES,
] as const;

export type Wf016WorkflowFolderEventType =
  (typeof WF_016_WORKFLOW_FOLDER_EVENT_TYPES)[number];

export type Wf016WorkflowVariableEventType =
  (typeof WF_016_WORKFLOW_VARIABLE_EVENT_TYPES)[number];

export type Wf016WorkflowGoalEventType =
  (typeof WF_016_WORKFLOW_GOAL_EVENT_TYPES)[number];

export type Wf016EventType = (typeof WF_016_EVENT_TYPES)[number];

/**
 * Closed catalog of variable value types. Mirrors
 * `WF-016-DECISION-PACKET.md` §"Phase 1 — workflow_variables.value_type"
 * verbatim. Future `workflow_variables` Drizzle column CHECK constraint
 * MUST inline this same tuple so the runtime can never persist an
 * unrecognised value type.
 *
 * - `string` — UTF-8 text (default for `{{var.name}}` substitution)
 * - `number` — IEEE-754 double (drives numeric action config)
 * - `boolean` — true/false (drives branch conditions)
 * - `json` — arbitrary JSON-serialisable structure (advanced use)
 */
export const WF_016_VARIABLE_VALUE_TYPE_VOCAB = [
  "string",
  "number",
  "boolean",
  "json",
] as const;

export type Wf016VariableValueTypeVocab =
  (typeof WF_016_VARIABLE_VALUE_TYPE_VOCAB)[number];

/**
 * Closed catalog of goal progress statuses. Mirrors
 * `WF-016-DECISION-PACKET.md` §"Phase 2 — workflow-goals.ts" verbatim.
 * The future `GET /workflows/:id/goals/progress` aggregator emits one
 * of these statuses per enrolment, and the `workflow.goal.progressed@v1`
 * payload carries the same vocab.
 *
 * - `not_started` — enrollment has not yet hit a step that affects the goal
 * - `in_progress` — at least one step has nudged progress > 0 but < target
 * - `achieved` — target reached (terminal — emits goal-achieved fan-out)
 * - `abandoned` — enrolment ended (cancelled / completed) before achieve
 */
export const WF_016_GOAL_STATUS_VOCAB = [
  "not_started",
  "in_progress",
  "achieved",
  "abandoned",
] as const;

export type Wf016GoalStatusVocab = (typeof WF_016_GOAL_STATUS_VOCAB)[number];

/**
 * Closed catalog of variable scopes. Mirrors decision packet
 * §"workflow_variables.workflow_id (nullable for tenant-global)"
 * verbatim. Drives the partial-unique index strategy:
 *
 * - `tenant` — `workflow_id IS NULL` — name unique per tenant
 * - `workflow` — `workflow_id IS NOT NULL` — name unique per workflow
 */
export const WF_016_VARIABLE_SCOPE_VOCAB = ["tenant", "workflow"] as const;

export type Wf016VariableScopeVocab =
  (typeof WF_016_VARIABLE_SCOPE_VOCAB)[number];

/* -------------------------------------------------------------------------- */
/* Runtime narrowing guards                                                   */
/* -------------------------------------------------------------------------- */

export function isWf016EventType(value: unknown): value is Wf016EventType {
  return (
    typeof value === "string" &&
    (WF_016_EVENT_TYPES as readonly string[]).includes(value)
  );
}

export function isWf016WorkflowFolderEventType(
  value: unknown,
): value is Wf016WorkflowFolderEventType {
  return (
    typeof value === "string" &&
    (WF_016_WORKFLOW_FOLDER_EVENT_TYPES as readonly string[]).includes(value)
  );
}

export function isWf016WorkflowVariableEventType(
  value: unknown,
): value is Wf016WorkflowVariableEventType {
  return (
    typeof value === "string" &&
    (WF_016_WORKFLOW_VARIABLE_EVENT_TYPES as readonly string[]).includes(value)
  );
}

export function isWf016WorkflowGoalEventType(
  value: unknown,
): value is Wf016WorkflowGoalEventType {
  return (
    typeof value === "string" &&
    (WF_016_WORKFLOW_GOAL_EVENT_TYPES as readonly string[]).includes(value)
  );
}

export function isWf016VariableValueTypeVocab(
  value: unknown,
): value is Wf016VariableValueTypeVocab {
  return (
    typeof value === "string" &&
    (WF_016_VARIABLE_VALUE_TYPE_VOCAB as readonly string[]).includes(value)
  );
}

export function isWf016GoalStatusVocab(
  value: unknown,
): value is Wf016GoalStatusVocab {
  return (
    typeof value === "string" &&
    (WF_016_GOAL_STATUS_VOCAB as readonly string[]).includes(value)
  );
}

export function isWf016VariableScopeVocab(
  value: unknown,
): value is Wf016VariableScopeVocab {
  return (
    typeof value === "string" &&
    (WF_016_VARIABLE_SCOPE_VOCAB as readonly string[]).includes(value)
  );
}

/* -------------------------------------------------------------------------- */
/* Per-event payload contracts                                                */
/* -------------------------------------------------------------------------- */

/**
 * Base payload carried by every WF-016 event. `tenantId` is mandatory
 * for fan-out + isolation; `actorUserId` is mandatory because every
 * folder/variable/goal mutation is operator-initiated (no system actor
 * for these surfaces in the Phase 2 envelope).
 */
export interface Wf016BaseEventPayload {
  tenantId: string;
  actorUserId: string;
}

/* -- Folder events --------------------------------------------------------- */

export interface Wf016WorkflowFolderCreatedPayload
  extends Wf016BaseEventPayload {
  folderId: string;
  name: string;
  description: string | null;
  color: string | null;
  createdAt: string;
}

export interface Wf016WorkflowFolderUpdatedPayload
  extends Wf016BaseEventPayload {
  folderId: string;
  /** Fields that changed in this update (closed allowlist). */
  changedFields: readonly ("name" | "description" | "color")[];
  updatedAt: string;
}

export interface Wf016WorkflowFolderDeletedPayload
  extends Wf016BaseEventPayload {
  folderId: string;
  /**
   * Number of workflows previously bound to this folder. Routed to
   * `workflows.folder_id` ON DELETE SET NULL per decision-packet
   * Phase 1 schema.
   */
  unboundWorkflowCount: number;
  deletedAt: string;
}

/* -- Variable events ------------------------------------------------------- */

export interface Wf016WorkflowVariableDefinedPayload
  extends Wf016BaseEventPayload {
  variableId: string;
  scope: Wf016VariableScopeVocab;
  /** Nullable when `scope === "tenant"`. */
  workflowId: string | null;
  name: string;
  valueType: Wf016VariableValueTypeVocab;
  /** Whether a default value was set at definition time. */
  hasDefaultValue: boolean;
  definedAt: string;
}

export interface Wf016WorkflowVariableUpdatedPayload
  extends Wf016BaseEventPayload {
  variableId: string;
  changedFields: readonly ("name" | "value_type" | "default_value")[];
  updatedAt: string;
}

export interface Wf016WorkflowVariableDeletedPayload
  extends Wf016BaseEventPayload {
  variableId: string;
  scope: Wf016VariableScopeVocab;
  workflowId: string | null;
  deletedAt: string;
}

/* -- Goal events ----------------------------------------------------------- */

export interface Wf016WorkflowGoalDefinedPayload extends Wf016BaseEventPayload {
  goalId: string;
  workflowId: string;
  name: string;
  /** Event type the goal counts (e.g. `payment.completed@v1`). */
  targetEventType: string;
  /** Optional revenue target in cents. */
  targetValueCents: number | null;
  definedAt: string;
}

export interface Wf016WorkflowGoalUpdatedPayload extends Wf016BaseEventPayload {
  goalId: string;
  workflowId: string;
  changedFields: readonly (
    | "name"
    | "target_event_type"
    | "target_value_cents"
  )[];
  updatedAt: string;
}

export interface Wf016WorkflowGoalDeletedPayload extends Wf016BaseEventPayload {
  goalId: string;
  workflowId: string;
  deletedAt: string;
}

export interface Wf016WorkflowGoalProgressedPayload
  extends Wf016BaseEventPayload {
  goalId: string;
  workflowId: string;
  enrollmentId: string;
  status: Wf016GoalStatusVocab;
  /** Cents accumulated toward `targetValueCents` (null for non-revenue). */
  progressValueCents: number | null;
  progressedAt: string;
}

/**
 * Discriminated union mapping each event type to its payload. Consumers
 * (route emitter, materializer, DLQ, audit-runner) `switch (event.type)`
 * and pull the right payload without any-casting.
 */
export type Wf016Event =
  | { type: "workflow.folder.created@v1"; payload: Wf016WorkflowFolderCreatedPayload }
  | { type: "workflow.folder.updated@v1"; payload: Wf016WorkflowFolderUpdatedPayload }
  | { type: "workflow.folder.deleted@v1"; payload: Wf016WorkflowFolderDeletedPayload }
  | {
      type: "workflow.variable.defined@v1";
      payload: Wf016WorkflowVariableDefinedPayload;
    }
  | {
      type: "workflow.variable.updated@v1";
      payload: Wf016WorkflowVariableUpdatedPayload;
    }
  | {
      type: "workflow.variable.deleted@v1";
      payload: Wf016WorkflowVariableDeletedPayload;
    }
  | { type: "workflow.goal.defined@v1"; payload: Wf016WorkflowGoalDefinedPayload }
  | { type: "workflow.goal.updated@v1"; payload: Wf016WorkflowGoalUpdatedPayload }
  | { type: "workflow.goal.deleted@v1"; payload: Wf016WorkflowGoalDeletedPayload }
  | {
      type: "workflow.goal.progressed@v1";
      payload: Wf016WorkflowGoalProgressedPayload;
    };
